/**
 * Types for the /downloads page (software-installer releases).
 *
 * The catalog row composes the generated Supabase type (STD-008) so column
 * drift fails `tsc`, not prod. NOT the firmware `releases` table — that one
 * belongs to the UC Flasher.
 */
import type { Database } from './supabase';

/** Columns of `public.software_products` a release carries for display.
 *
 * Populated by the server-side PostgREST embed on `GET /api/software-releases`
 * only — `/latest` and the finalize response omit it, and it is absent in the
 * window between a deploy and its migration apply, where the backend degrades
 * to a plain select. So it is a *preferred* source, never a required one:
 * `resolveProductLabel` falls back to the bundled map and then to the raw key. */
export interface SoftwareProductEmbed {
  label: string;
  sort_order: number;
}

export type SoftwareRelease = Database['public']['Tables']['software_releases']['Row'] & {
  software_products?: SoftwareProductEmbed | null;
};

export type SoftwareAudience = 'customer' | 'xer_internal';

/** A row of `public.software_products` (XER-admin surface).
 *
 * Hand-written rather than taken from `Database[...]`: the table reaches
 * `types/supabase.ts` only after `scripts/db-sync.sh` runs against prod, which
 * happens after this migration merges. Swap it for the generated Row type then. */
export interface SoftwareProductRow {
  key: string;
  label: string;
  audience: SoftwareAudience;
  sort_order: number;
  archived: boolean;
  created_by?: string | null;
  created_at?: string | null;
}

export interface SoftwareProductListResponse {
  products: SoftwareProductRow[];
}

export interface CreateProductInput {
  key: string;
  label: string;
  audience: SoftwareAudience;
}

export interface UpdateProductInput {
  label?: string;
  sort_order?: number;
  archived?: boolean;
}

/** The four products that existed before the catalog became a table.
 *
 * NOT the set of valid products any more — products are rows and an XER admin
 * creates one without a deploy. This union is only the key space of the
 * bundled fallback label map below. */
export type SoftwareProduct = 'missionplanner' | 'uc_flasher_operator' | 'uc_flasher' | 'h16_qgc';

/** Bundled FALLBACK labels, for when the server sent none (an older bundle, a
 * `/latest` response, the pre-migration window). They match the flasher's own
 * per-variant `productName` (electron-builder.js) so the download page names a
 * build the same way the installed app does. `uc_flasher` is the engineer build
 * — internal-only, it embeds a firmware-repo PAT; `uc_flasher_operator` is the
 * customer build. The authoritative label is `software_products.label`. */
export const SOFTWARE_PRODUCT_LABELS: Record<SoftwareProduct, string> = {
  missionplanner: 'XER Mission Planner',
  uc_flasher_operator: 'XER UC Flasher',
  uc_flasher: 'XER UC Flasher (Engineer)',
  h16_qgc: 'XER H16 QGC',
};

/** Fallback column ordering, used only for products the server sent no
 * `sort_order` for. /downloads renders only the products present in the API
 * response — the API/RLS decides visibility (audience gating: internal
 * products are absent for customers), so this list must never be used to
 * enumerate columns. */
export const SOFTWARE_PRODUCT_ORDER: SoftwareProduct[] = [
  'missionplanner',
  'uc_flasher_operator',
  'uc_flasher',
  'h16_qgc',
];

/** Display label: the server's label if there is one, then the bundled map,
 * then the raw key — so a product created after this bundle was built renders
 * its real name, and an unknown one renders its key rather than `undefined`. */
export function resolveProductLabel(product: string, serverLabel?: string | null): string {
  return serverLabel ?? SOFTWARE_PRODUCT_LABELS[product as SoftwareProduct] ?? product;
}

// `productLabel(product)` and `orderProducts(products)` lived here and are
// gone: both answered from the bundled maps alone, which cannot name or place
// a product created after the bundle was built. Their replacements take the
// server's answer first and fall back to these constants —
// `resolveProductLabel` above, and `orderProductsFor` /
// `releaseProductLabel` in `components/Downloads/productCatalog.ts`.

export interface SoftwareReleaseListResponse {
  releases: SoftwareRelease[];
}

export interface DownloadLinkResponse {
  download_url: string;
  expires_in: number;
  file_name: string;
  sha256: string;
  size_bytes: number;
}

/** What `POST /api/software-releases/upload` answers once the installer's bytes
 * are in S3. `size_bytes` is measured server-side from what was actually
 * written, not echoed back from the client's declaration. */
export interface ReleaseUploadResponse {
  s3_key: string;
  size_bytes: number;
}

export interface PublishReleaseInput {
  /** A product KEY, not one of the four legacy literals — the publish dropdown
   * is built from `GET /api/software-releases/products`, so a product created
   * this morning is publishable from a bundle built last month. */
  product: string;
  version: string;
  file_name: string;
  size_bytes: number;
  channel: string;
  release_notes?: string | null;
}

export interface DeleteReleaseResponse {
  success: boolean;
  deleted: string;
}
